import { ActionTree } from 'vuex';
import { httpClientWithCancel } from '@/httpClient/httpClient';
import { IDashboardState } from '@/types/dashboard';
import {
  EStatus,
  ResponseBody,
  ResponseDataProvider,
  RootState,
} from '@/types';
import ResponseDataValue from '@/utils/ResponseDataValue';

const actions: ActionTree<IDashboardState, RootState> = {
  async loadRunningCampaignsCount({ commit }) {
    commit('changeLoader', ['runningCampaignsCount', true]);

    const sortData = {
      status: EStatus.ACTIVE,
      per__page: 0,
      fields: 'id',
    };

    try {
      const response: ResponseBody = await httpClientWithCancel().get('/campaign', {
        params: sortData,
      });

      const responseDataProvider: ResponseDataProvider = ResponseDataValue(
        response,
        true
      );

      commit('setRunningCampaignsCount', responseDataProvider?.data);
      return responseDataProvider;
    } catch (err) {
      commit('changeLoader', ['runningCampaignsCount', false]);
      console.error('loadRunningCampaignsCount error: ', err);
    }
  },
  async loadSpendCount({ commit }) {
    try {
      commit('changeLoader', ['spendCount', true]);

      const FIELD = 'sum_revenue';

      const responseToday: ResponseBody = await loadTodayStatistics(FIELD);
      const responseYesterday: ResponseBody = await loadYesterdayStatistics(
        FIELD
      );

      commit('setSpendCount', [
        responseToday.data.total[FIELD],
        responseYesterday.data.total[FIELD],
      ]);
    } catch (err) {
      commit('changeLoader', ['spendCount', false]);
      console.error('loadSpendCount error: ', err);
    }
  },
  async loadImpressionsCount({ commit }) {
    commit('changeLoader', ['impressionsCount', true]);

    const FIELD = 'count_view';

    try {
      const responseToday: ResponseBody = await loadTodayStatistics(FIELD);
      const responseYesterday: ResponseBody = await loadYesterdayStatistics(
        FIELD
      );

      commit('setImpressionsCount', [
        responseToday.data.total[FIELD],
        responseYesterday.data.total[FIELD],
      ]);
    } catch (err) {
      commit('changeLoader', ['impressionsCount', false]);
      console.error('loadImpressionsCount error: ', err);
    }
  },
  async loadConversionsCount({ commit }) {
    commit('changeLoader', ['conversionsCount', true]);

    const FIELD = 'count_lead';

    try {
      const responseToday: ResponseBody = await loadTodayStatistics(FIELD);
      const responseYesterday: ResponseBody = await loadYesterdayStatistics(
        FIELD
      );

      commit('setConversionsCount', [
        responseToday.data.total[FIELD],
        responseYesterday.data.total[FIELD],
      ]);
    } catch (err) {
      commit('changeLoader', ['conversionsCount', false]);
      console.error('loadCounversionsCount error: ', err);
    }
  },
  async loadChart({ commit, getters }, field) {
    try {
      let controller = getters['getController']('chart');
      if (controller) {
        controller.abort();
      }

      controller = new AbortController();
      commit('changeLoader', ['chart', true]);
      commit('changeController', ['chart', controller]);

      const COUNT_CAMPAIGNS = 5;
      const DAY_PERIOD_IN_MINUTES = 1440;
      const todayDate = getTodayDate();
      const weekAgoDate = getWeekAgoDate();

      const params = {
        to_date: todayDate,
        from_date: weekAgoDate,
        per__page: COUNT_CAMPAIGNS,
        detail_period: DAY_PERIOD_IN_MINUTES,
        sort: '-' + field,
        fields: 'name,' + field,
      };

      const response: ResponseBody = await httpClientWithCancel().get(
        '/statistics/detail-campaign',
        {
          params,
          signal: controller.signal,
        }
      );

      commit('setChart', response?.data);
    } catch (err) {
      commit('changeLoader', ['chart', false]);
      console.error('loadChart error: ', err);
    }
  },
  async loadSspTable({ commit, getters }, data) {
    try {
      let controller = getters['getController']('sspTable');
      if (controller) {
        controller.abort();
      }

      controller = new AbortController();
      commit('changeLoader', ['sspTable', true]);
      commit('changeController', ['sspTable', controller]);

      const COUNT_CAMPAIGNS = 50;
      const todayDate = getTodayDate();

      const params = {
        to_date: todayDate,
        from_date: todayDate,
        per__page: COUNT_CAMPAIGNS,
        sort: data.sort,
        fields: data.fields,
      };

      const response: ResponseBody = await httpClientWithCancel().get(
        '/statistics/ssp',
        {
          params,
          signal: controller.signal,
        }
      );

      commit('setSspTable', response?.data);
    } catch (err) {
      commit('changeLoader', ['sspTable', false]);
      console.error('loadSspTable error: ', err);
    }
  },
  async loadSiteDomainsTable({ commit, getters }, data) {
    try {
      let controller = getters['getController']('siteDomainsTable');
      if (controller) {
        controller.abort();
      }

      controller = new AbortController();
      commit('changeLoader', ['siteDomainsTable', true]);
      commit('changeController', ['siteDomainsTable', controller]);

      const COUNT_CAMPAIGNS = 50;
      const todayDate = getTodayDate();

      const params = {
        to_date: todayDate,
        from_date: todayDate,
        per__page: COUNT_CAMPAIGNS,
        sort: data.sort,
        fields: data.fields,
      };

      const response: ResponseBody = await httpClientWithCancel().get(
        '/statistics/site',
        {
          params,
          signal: controller.signal,
        }
      );

      commit('setSiteDomainsTable', response);
    } catch (err) {
      commit('changeLoader', ['siteDomainsTable', false]);
      console.error('loadSiteDomainsTable error: ', err);
    }
  },
  async loadPieChart({ commit, getters }, field = 'name,sum_revenue') {
    try {
      let controller = getters['getController']('pieChart');
      if (controller) {
        controller.abort();
      }

      controller = new AbortController();
      commit('changeLoader', ['pieChart', true]);
      commit('changeController', ['pieChart', controller]);

      const todayDate = getTodayDate();

      const params = {
        to_date: todayDate,
        from_date: todayDate,
        status: 'active',
        fields: field,
      };

      const response: ResponseBody = await httpClientWithCancel().get(
        '/statistics/campaign',
        {
          params,
          signal: controller.signal,
        }
      );

      commit('setPieChart', response?.data);
    } catch (err) {
      commit('changeLoader', ['pieChart', false]);
      console.error('loadPieChart error: ', err);
    }
  },
};

function loadTodayStatistics(fields) {
  const todayDate = getTodayDate();
  const sortData = {
    to_date: todayDate,
    from_date: todayDate,
    fields,
  };

  return sendStatisticsRequest(sortData);
}

function loadYesterdayStatistics(fields) {
  const yesterdayDate = getYesterdayDate();
  const sortData = {
    to_date: yesterdayDate,
    from_date: yesterdayDate,
    fields,
  };

  return sendStatisticsRequest(sortData);
}

function sendStatisticsRequest(sortData) {
  return httpClientWithCancel().get('/statistics/campaign', {
    params: sortData,
  });
}

function getTodayDate() {
  const date = new Date();

  return formatDate(date);
}

function getYesterdayDate() {
  const ONE_DAY = 1;
  const date = new Date();
  date.setDate(date.getDate() - ONE_DAY);

  return formatDate(date);
}

function getWeekAgoDate() {
  const SIX_DAYS = 6;
  const date = new Date();
  date.setDate(date.getDate() - SIX_DAYS);

  return formatDate(date);
}

// format date 00.00.0000
function formatDate(date) {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  return `${day > 0 ? day : '0' + day}.${
    month > 0 ? month : '0' + month
  }.${year}`;
}

export default actions;
